/* eslint-disable no-return-await */
import { IncomingHttpHeaders } from 'http';
import {
  RailwayCompanyResponse,
  AreaResponse,
  QualificationResponse,
} from '@doda/api-client/aspida/@types';
import { apiClient } from '@doda/api-client/util/apiClient';
import MasterCacheModel, {
  AreaListOfSearchModal,
  CityOfSearchModal,
  CompanyOfSearchModal,
  QualificationData,
  QualificationMap,
} from '@doda/common/src/models/MasterCacheModel';
import {
  getJobType,
  getJobTypeMasterSort,
} from '../services/master/getJobTypeAPI';
import { getIndustry } from '../services/master/getIndustryAPI';
import {
  getIndustry as getIndustryV2,
  getIndustryMasterSort,
} from '../services/master/v2/getIndustryAPI';
import { getArea } from '../services/master/getAreaAPI';
import { getRailwayCompany } from '../services/master/getRailwayCompanyAPI';
import { reorderArray } from '../utils/arrayUtil';

const JOB_TYPE_ORDER_ID_LIST = [
  '01', // 営業職
  '02', // 企画・管理
  '03', // 技術職（SE・インフラエンジニア・Webエンジニア）
  '14', // 技術職（組み込みソフトウェア）
  '04', // 技術職（機械・電気）
  '05', // 技術職（化学・素材・化粧品・トイレタリー）
  '15', // 技術職（食品・香料・飼料）
  '06', // 技術職・専門職（建設・建築・不動産・プラント・工場）
  '07', // 専門職（コンサルティングファーム・専門事務所・監査法人）
  '08', // クリエイター・クリエイティブ職
  '09', // 販売・サービス職
  '10', // 公務員・教員・農林水産関連職
  '11', // 事務・アシスタント
  '12', // 医療系専門職
  '13', // 金融系専門職
];

const INDUSTRY_ORDER_ID_LIST = [
  '01', // IT・通信業界
  '11', // インターネット・広告・メディア業界
  '02', // メーカー（機械・電気）業界
  '03', // メーカー（素材・化学・食品・化粧品・その他）業界
  '05', // 商社業界
  '06', // 医薬品・医療機器・ライフサイエンス・医療系サービス
  '07', // 金融業界
  '08', // 建設・プラント・不動産業界
  '09', // コンサルティング・リサーチ業界・専門事務所・監査法人・税理士法人
  '10', // 人材サービス・アウトソーシング業界・コールセンター// 小売業界
  '12', // 小売業界
  '24', // 外食産業・飲食業界
  '13', // 運輸・物流業界
  '14', // エネルギー（電力・ガス・石油・新エネルギー）業界
  '15', // 旅行・宿泊・レジャー業界
  '16', // 警備・清掃業界
  '17', // 理容・美容・エステ業界
  '18', // 教育業界
  '21', // 農林水産・鉱業
  '22', // 公社・官公庁・学校・研究施設
  '25', // 冠婚葬祭業界
  '23', // その他
];

export const saveOccupationCache = async (headers: IncomingHttpHeaders) => {
  const oc = MasterCacheModel.occupation;
  if (oc) {
    return oc;
  }
  return await getJobType(headers).then((data) => {
    const orderArray = reorderArray(
      data,
      'searchJobTypeId',
      JOB_TYPE_ORDER_ID_LIST
    );

    MasterCacheModel.occupation = orderArray;
    return data;
  });
};

export const saveIndustryCache = async (headers: IncomingHttpHeaders) => {
  const ind = MasterCacheModel.industry;
  if (ind) {
    return ind;
  }
  return await getIndustry(headers).then((data) => {
    const orderArray = reorderArray(data, 'id', INDUSTRY_ORDER_ID_LIST);
    MasterCacheModel.industry = orderArray;
    return data;
  });
};

export const saveAreaCache = async (headers: IncomingHttpHeaders) => {
  const { area } = MasterCacheModel;
  if (area) {
    return area;
  }
  return await getArea(headers).then((data) => {
    // eslint-disable-next-line no-shadow
    const { area, city } = separateAreaAndCity(data);
    MasterCacheModel.area = area;
    MasterCacheModel.city = city;
    return data;
  });
};
export const saveRouteCache = async (headers: IncomingHttpHeaders) => {
  const { route } = MasterCacheModel;
  if (route) {
    return route;
  }
  return await getRailwayCompany(undefined, headers).then((data) => {
    MasterCacheModel.route = assignPrefIdList(data);
    return data;
  });
};

export const cacheQualificationMaster = (headers: IncomingHttpHeaders) => {
  const qc = MasterCacheModel.qualification;
  if (qc) return qc;
  return apiClient.api.v1.qualifications
    .$get({ config: { headers } })
    .then((data) => {
      MasterCacheModel.qualification = data;
      MasterCacheModel.qualificationMap = normalizeQualification(data);
      return data;
    });
};

export const cacheMasterDataOfsearchConditionModal = async (
  headers: IncomingHttpHeaders
) => {
  return Promise.allSettled([
    saveOccupationCache(headers),
    saveIndustryCache(headers),
    saveAreaCache(headers),
    saveRouteCache(headers),
  ]);
};

/**
 * PCの検索サイドバー用マスターデータキャッシュ処理
 * TODO cacheMasterDataOfsearchConditionModalと要統合
 * @param headers HttpHeader
 * @returns Promise
 */
export const cacheMasterDataForPcSidebar = async (
  headers: IncomingHttpHeaders
) => {
  return Promise.allSettled([
    saveOccupationCache(headers),
    saveIndustryCache(headers),
    saveAreaFullDataCache(headers),
    saveRouteFullDataCache(headers),
  ]);
};

/**
 * プレカウンセリングアンケート用マスターデータキャッシュ処理
 * @param headers HttpHeader
 * @returns Promise
 */
export const cacheMasterDataForPrecounseling = async (
  headers: IncomingHttpHeaders
) => {
  return Promise.allSettled([
    saveOccupationCache(headers),
    saveIndustryV2Cache(headers),
    saveAreaCache(headers),
  ]);
};

export const saveAreaFullDataCache = async (headers: IncomingHttpHeaders) => {
  const { area: cachedArea, areaFullData: cachedAreaFullData } =
    MasterCacheModel;

  // キャッシュがすでにある場合は早期リターン
  if (cachedArea && cachedAreaFullData) return;

  const areaData = await getArea(headers);

  if (!cachedArea) {
    const { area, city } = separateAreaAndCity(areaData);
    MasterCacheModel.area = area;
    MasterCacheModel.city = city;
  }

  if (!cachedAreaFullData) MasterCacheModel.areaFullData = areaData;
};

const saveRouteFullDataCache = async (headers: IncomingHttpHeaders) => {
  const { route: cachedRoute, routeFullData: cachedRouteFullData } =
    MasterCacheModel;

  // キャッシュがすでにある場合は早期リターン
  if (cachedRoute && cachedRouteFullData) return;

  const routeData = await getRailwayCompany(undefined, headers);

  if (!cachedRoute) {
    MasterCacheModel.route = assignPrefIdList(routeData);
  }

  if (!cachedRouteFullData) MasterCacheModel.routeFullData = routeData;
};

export const saveIndustryV2Cache = async (headers: IncomingHttpHeaders) => {
  const ind = MasterCacheModel.industryV2;
  if (ind) {
    return ind;
  }
  return await getIndustryV2(headers).then((data) => {
    MasterCacheModel.industryV2 = data;
    return data;
  });
};

export const saveIndustryV2CacheMasterSort = async (
  headers: IncomingHttpHeaders
) => {
  const ind = MasterCacheModel.industryV2MasterSort;
  if (ind) {
    return ind;
  }
  return await getIndustryMasterSort(headers).then((data) => {
    MasterCacheModel.industryV2MasterSort = data;
    return data;
  });
};

export const saveOccupationCacheMasterSort = async (
  headers: IncomingHttpHeaders
) => {
  const oc = MasterCacheModel.occupationMasterSort;
  if (oc) {
    return oc;
  }
  return await getJobTypeMasterSort(headers).then((data) => {
    MasterCacheModel.occupationMasterSort = data;
    return data;
  });
};

export const assignPrefIdList = (companyList: RailwayCompanyResponse[]) => {
  const newRoute = [...companyList] as CompanyOfSearchModal[];

  companyList.forEach((company, i) => {
    let compPrefIdList: string[] = [];
    company.routeList.forEach((route, j) => {
      const routePrefIdList: string[] = [];
      route.stationList.forEach((station) => {
        const prefId = station.prefectureId;
        if (!routePrefIdList.includes(prefId)) {
          routePrefIdList.push(prefId);
        }
      });
      newRoute[i].routeList[j].prefIdList = routePrefIdList;
      compPrefIdList = compPrefIdList.concat(routePrefIdList);
    });
    newRoute[i].prefIdList = Array.from(new Set(compPrefIdList));
  });

  return newRoute;
};

export const separateAreaAndCity = (areaList: AreaResponse[]) => {
  const newAreaList: AreaListOfSearchModal[] = [];
  const newCityList: CityOfSearchModal[] = [];
  if (areaList) {
    areaList.forEach((area) => {
      const newArea: AreaListOfSearchModal = {
        id: String(area.id),
        name: area.name.replace(/^九州$/, '九州・沖縄'),
        prefectureList: [],
      };
      area.prefectureList.forEach((pref) => {
        const newPref = { id: String(pref.id), name: pref.name };
        newArea.prefectureList.push(newPref);
        pref.cityList.forEach((city) => {
          const newCity: CityOfSearchModal = {
            id: String(city.id),
            name: city.name,
            prefId: newPref.id,
            isOrdinanceDesignatedCity: city.isOrdinanceDesignatedCity,
            wardList: [],
          };
          city.wardList.forEach((ward) => {
            newCity.wardList.push({ id: String(ward.id), name: ward.name });
          });
          newCityList.push(newCity);
        });
      });
      newAreaList.push(newArea);
    });
  }
  return { area: newAreaList, city: newCityList };
};

/**
 * 資格マスタの正規化
 */
export function normalizeQualification(data: QualificationResponse[]) {
  const qualificationMap: QualificationMap = new Map();

  for (let i = 0; i < data.length; i += 1) {
    const categoryData = data[i];
    const { qualifications } = categoryData;
    // eslint-disable-next-line no-continue
    if (!qualifications) continue;
    // eslint-disable-next-line no-shadow
    for (let i = 0; i < qualifications.length; i += 1) {
      const qualification = qualifications[i];
      const { id, ...rest } = qualification;
      // eslint-disable-next-line no-continue
      if (!id) continue;
      const normalizedData: QualificationData = {
        categoryId: categoryData.id || 'NO ID',
        qualificationName: rest.name || 'NO NAME',
        ranks: rest.ranks || [],
      };
      qualificationMap.set(id, normalizedData);
    }
  }

  return qualificationMap;
}
